import { Client, Conversation, Participant, Message, User } from "@twilio/conversations";
import { GenericThemeShape } from "@twilio-paste/theme";
import type { AlertProps } from "@twilio-paste/core/alert";

import { FileAttachmentConfig } from "../definitions";

export enum EngagementPhase {
    PreEngagementForm = "PreEngagementForm",
    MessagingCanvas = "MessagingCanvas",
    Loading = "Loading"
}

export type ChatState = {
    conversationsClient?: Client;
    conversation?: Conversation;
    participants?: Participant[];
    users?: User[];
    messages?: Message[];
    attachedFiles?: File[];
    conversationState?: string;
    /* "Esperando o bot": trava input/botões e mostra o balão de digitando (ver TypingIndicator). */
    awaitingBot?: boolean;
    /* Trava fixa (~3s) ao iniciar a conversa, cobrindo o carregamento do fluxo. Timer, não evento. */
    welcomeInputLock?: boolean;
    /* Modo humano (pós-transbordo): atendente assumiu — nunca trava input nem mostra balão. */
    humanMode?: boolean;
    agentTyping?: boolean;
    /*
     * SINAL de que existe pesquisa pendente — NÃO é a pesquisa.
     *
     * O snapshot autoritativo (escala, allowEdit, answered, expiresAt) vive em `csat`, e vem do
     * GET /pending. Este campo diz apenas "vale a pena perguntar agora". Os dois são separados de
     * propósito: o `useCsat` só busca quando `csat` está ausente, então escrever o sinal lá dentro
     * impediria a busca do snapshot para sempre.
     */
    csatPending?: boolean;
    /* Pesquisa CSAT desta conversa (v2): semeada pelo GET /pending (snapshot) + progresso do envio. */
    csat?: CsatChatState;
};

/* Progresso local do envio do CSAT. `readonly` = terminal "obrigado" (410/409/403/400). */
export type CsatStatus = "idle" | "loading" | "submitting" | "submitted" | "readonly" | "retrying";

/*
 * Estado da pesquisa CSAT numa conversa. `scale`/`allowEdit`/`expiresAt`/`answered`/`score`/`comment`
 * vêm do GET /pending (SNAPSHOT autoritativo — o render usa isto, não o brand-config). `status` é local.
 */
export type CsatChatState = {
    requested: boolean;
    channel: "ai" | "human";
    scale: CsatScale;
    allowEdit: boolean;
    expiresAt: string; // ISO 8601 UTC "Z"
    answered: boolean;
    score?: number; // pré-preenche a edição pós-reload
    comment?: string;
    status: CsatStatus;
    /*
     * NOSSO, não do fio — e o nome em português marca isso. Todo campo acima veio do GET /pending;
     * este nasce aqui, quando o envio dá certo com a conversa ainda viva.
     *
     * Ele existe para a despedida saber que não tem nada a dizer a quem já avaliou durante o
     * atendimento. Sem ele, `answered` e `status` ficam idênticos nos dois casos, e a despedida
     * repetiria o agradecimento de uma ação que a pessoa fez minutos antes, em outro ponto da tela.
     */
    respondidaNoAtendimento?: boolean;
    /*
     * NOSSO também, e pelo mesmo motivo do de cima: a faixa do atendimento já mostrou o
     * agradecimento e se aposentou.
     *
     * Ele existe porque o synccx desliga `csatPending` no instante em que grava a nota, e a faixa
     * monta por esse sinal — o desligamento levava embora o agradecimento recém-renderizado. Com
     * este campo quem decide a hora de sumir é o card, depois de a mensagem ter sido vista, e o
     * desligamento do outro lado passa a ser inofensivo.
     */
    faixaAposentada?: boolean;
};

/*
 * Dados coletados no formulário pré-chat. Chaves DINÂMICAS (definidas pelo brand-config `prechat.form`),
 * indexadas por `field.key`. Os defaults usam `friendlyName` e `cpf` (contrato do submit → attributes).
 */
export type PreEngagementData = Record<string, string>;

/* ===== Personalização das telas pré-chat (brand-config `prechat`, runtime, best-effort) ===== */

export type PrechatFieldType = "text" | "cpf" | "email" | "phone";

export type PrechatField = {
    key: string;
    label: string;
    placeholder?: string;
    required: boolean;
    type: PrechatFieldType;
};

export type PrechatCard = {
    id: string;
    title: string;
    subtitle?: string;
    url?: string;
    enabled: boolean;
};

export type PrechatConfig = {
    welcome?: { title?: string; subtitle?: string };
    cards?: PrechatCard[];
    form?: { fields?: PrechatField[] };
    loading?: { spinnerColor?: string; message?: string };
};

export type SessionState = {
    currentPhase: EngagementPhase;
    expanded: boolean;
    token?: string;
    conversationSid?: string;
    conversationsClient?: Client;
    conversation?: Conversation;
    users?: User[];
    participants?: Participant[];
    messages?: Message[];
    conversationState?: "active" | "inactive" | "closed";
    preEngagementData?: PreEngagementData;
    preEngagementStep: number;
};

/* Posição configurável do launcher/janela (MVP: cantos inferiores). Contrato compartilhado com o dashboard. */
type LauncherCorner = "bottom-right" | "bottom-left";

export type LauncherPosition = {
    corner: LauncherCorner;
    offsetX: number; // px horizontal (right se *-right, left se *-left)
    offsetY: number; // px da borda inferior (bottom)
    sameOnMobile?: boolean; // usar a mesma posição no mobile (launcher). default true
};

/* ===== CSAT (pesquisa de satisfação) — v2 pull model. Ver docs/plano-csat-widget.md ===== */

/* Fonte única das escalas (runtime + tipo derivado). Adicionar uma escala = editar só esta linha. */
export const CSAT_SCALES = ["numbers", "stars", "faces", "thumbs"] as const;
export type CsatScale = (typeof CSAT_SCALES)[number];

/*
 * Config da marca (brand-config, runtime). Ausente / enabled != true = CSAT desligado.
 * ⚠️ `appearance.scale` e `window.allowEdit` existem no payload da marca, mas a escala/allowEdit
 * RENDERIZADOS numa pesquisa vêm do snapshot do GET /pending (ChatState.csat) — o config configura
 * pesquisas futuras; o render segue o snapshot. Ver plano §4.
 */
export type CsatConfig = {
    enabled: boolean;
    appearance: {
        title: string;
        /*
         * Texto da pesquisa disparada por macro DURANTE o atendimento. Opcional: a superfície é nova e
         * o dashboard ainda não a conhece, então o widget tem default próprio. O `title` acima não
         * serve nos dois lugares — ele está no passado ("como FOI"), e no meio do atendimento isso dá
         * a entender que acabou, que é a leitura que esta feature não pode induzir.
         */
        titleDuringChat?: string;
        thankYouText: string;
        scale: CsatScale;
        comment: { enabled: boolean; label: string; labelDuringChat?: string };
        primaryColor: string;
    };
    window: { allowEdit: boolean };
};

export type ConfigState = {
    serverUrl?: string;
    theme?: {
        isLight?: boolean;
        overrides?: Partial<GenericThemeShape>;
    };
    fileAttachment?: FileAttachmentConfig;
    /* Logo do launcher resolvido em runtime (dashboard, por marca). Fallback: BRAND.launcherLogo. */
    launcherLogo?: string;
    /* Posição do launcher/janela (dashboard, runtime). Ausente = posição atual (bottom-right/20/19). */
    launcherPosition?: LauncherPosition;
    /* Arredondamento (px) da janela do chat. Ausente = 20px (atual). */
    borderRadius?: number;
    /* Título do cabeçalho (dashboard, runtime). Ausente/vazio = default da marca ("Suporte 24 horas"). */
    headerTitle?: string;
    /* Personalização das telas pré-chat (dashboard, runtime). Ausente = telas hardcoded de hoje. */
    prechat?: PrechatConfig;
    /* CSAT por marca (dashboard, runtime). Ausente/desligado = card não renderiza. */
    csat?: CsatConfig;
    /** Quantas promoções estão no ar — desenha o contador do card na tela inicial. */
    promocoesVigentes?: number;
};

export type Notification = {
    dismissible: boolean;
    id: string;
    onDismiss?: () => void;
    message: string;
    timeout?: number;
    // Paste 21: o barril core/alert deixou de reexportar AlertVariants; derivar da prop
    // mantém o mesmo union ("error" | "neutral" | "warning") sem depender de pacote transitivo.
    type: AlertProps["variant"];
};

export type NotificationState = Notification[];

export type AppState = {
    chat: ChatState;
    config: ConfigState;
    session: SessionState;
    notifications: NotificationState;
};
